import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  Text,
  Progress,
  Button,
  VStack,
  HStack,
  Image,
  Box,
  useToast,
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { pxToRem } from "../../utils/editor";
import { FileService } from "../../services/fileService";

interface DoenbaImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess?: (paperId: string) => void;
}

type ImportStatus = "upload" | "importing" | "success" | "error";

export const DoenbaImportModal: React.FC<DoenbaImportModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess,
}) => {
  const [status, setStatus] = useState<ImportStatus>("upload");
  const [progress, setProgress] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const toast = useToast();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStatus("upload");
      setProgress(0);
      setFile(null);
    } else {
      cleanupTimer();
    }
    return cleanupTimer;
  }, [isOpen]);

  const cleanupTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const selectedFile = acceptedFiles[0];
        // Check extension
        if (!selectedFile.name.toLowerCase().endsWith(".edi")) {
          toast({
            title: "Invalid file type",
            description: "Please upload a .edi file.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return;
        }
        setFile(selectedFile);
        startImport(selectedFile);
      }
    },
    [toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      "application/octet-stream": [".edi"],
      "text/plain": [".edi"],
      "": [".edi"],
    },
  });

  const startImport = async (fileToImport: File) => {
    setStatus("importing");
    setProgress(0);
    cleanupTimer();

    // Start fake progress to 90%
    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return 90;
        return prev + 5;
      });
    }, 200);

    try {
      const response = await FileService.importEdiFile(fileToImport);

      if (timerRef.current) clearInterval(timerRef.current);
      setProgress(100);
      setStatus("success");
      toast({
        title: "Import Successful",
        description: `Imported "${response.paper_name}" successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      // Auto close or notify success
      setTimeout(() => {
        if (onImportSuccess) onImportSuccess(response.paper_id);
        onClose();
      }, 1000);
    } catch (error) {
      if (timerRef.current) clearInterval(timerRef.current);
      setStatus("error");
      toast({
        title: "Import Failed",
        description: "Failed to import the file. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  };

  const handleCancel = () => {
    cleanupTimer();
    onClose();
  };

  const isError = status === "error";

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} isCentered size="2xl">
      <ModalOverlay />
      <ModalContent borderRadius={pxToRem(20)} p={pxToRem(10)}>
        <ModalCloseButton color="gray.500" />
        <ModalBody p={pxToRem(30)}>
          {status === "upload" ? (
            <VStack align="stretch" spacing={4}>
              <Text fontSize={pxToRem(24)} fontWeight="bold" fontFamily="Lato">
                Import a .edi File
              </Text>
              <Text
                fontSize={pxToRem(16)}
                color="gray.500"
                fontFamily="Lato"
                mt={-2}
              >
                Upload .edi file to import the file into your account.
              </Text>

              <Box
                {...getRootProps()}
                border="2px dashed #E2E8F0"
                borderRadius="xl"
                h={pxToRem(250)}
                display="flex"
                flexDir="column"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                bg={isDragActive ? "blue.50" : "white"}
                transition="all 0.2s"
                _hover={{ borderColor: "blue.400", bg: "gray.50" }}
              >
                <input {...getInputProps()} />
                <Image
                  src="/static/editor/upload.svg"
                  alt="Upload"
                  w={pxToRem(48)}
                  mb={4}
                  // If svg doesn't work well or needs color override, we might need a specific Icon component
                  // but Image usually works if the svg is standard.
                />
                <Text fontSize={pxToRem(18)} color="gray.500" mb={2}>
                  {isDragActive ? "Drop the file here" : "Drag to Upload"}
                </Text>
                <Text fontSize={pxToRem(14)} color="gray.400" mb={6}>
                  Please upload .edi files
                </Text>
                <Button colorScheme="blue" px={8} borderRadius="full">
                  Select
                </Button>
              </Box>
            </VStack>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Text fontSize={pxToRem(24)} fontWeight="bold" fontFamily="Lato">
                Import a .edi File
              </Text>
              <Text
                fontSize={pxToRem(16)}
                color="gray.500"
                fontFamily="Lato"
                mt={-2}
              >
                Upload .edi file to import the paper into your account.
              </Text>

              <HStack spacing={4} mt={4} align="center">
                <Box
                  border="1px solid #E2E8F0"
                  borderRadius="md"
                  p={2}
                  w={pxToRem(60)}
                  h={pxToRem(70)}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image
                    src="/static/doenba-ico.png"
                    alt=".edi File"
                    w="80%"
                  />
                </Box>

                <VStack align="start" flex={1} spacing={1}>
                  <Text
                    fontWeight="bold"
                    fontSize={pxToRem(18)}
                    fontFamily="Lato"
                  >
                    Importing “{file?.name || "Unknown File"}”
                  </Text>

                  <Progress
                    value={progress}
                    size="sm"
                    w="100%"
                    borderRadius="full"
                    colorScheme={isError ? "red" : "blue"}
                    bg="#E2E8F0"
                  />

                  <HStack justify="space-between" w="100%">
                    {isError ? (
                      <Text color="red.500" fontSize="sm" mx="auto">
                        Import Failed
                      </Text>
                    ) : (
                      <Text color="gray.500" fontSize="sm" mx="auto">
                        {progress}% Completed
                      </Text>
                    )}
                  </HStack>
                </VStack>
              </HStack>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
